// ─────────────────────────────────────────────────────────────────────────────
// PreparingStep (2/3) — shown while POST /enterprise-nodes is in flight.
//
// The bar is INDETERMINATE and says so by never reaching 100%: it eases toward a
// ceiling and stays there until the request resolves. The parent owns the step
// change, so this screen cannot advance on its own.
//
// It used to be a 3.2s timer that auto-advanced to "Package Ready" — which
// announced success before any request had been made, and would have claimed a
// node existed even when the create failed. Completion is the server's to report,
// not this component's.
//
// The endpoint does real work here (geo lookup + a reachability probe against the
// address), so a slow response is normal rather than a sign of trouble.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import styles from "./AddNodeWizard.module.css";
import { SpinnerIcon } from "./icons";

const TICK_MS = 90;
const CEILING = 92;      // never 100 — only the response can finish this
const EASE = 0.045;      // approach rate: fast at first, crawling near the ceiling

const buildLog = (nodeName) => [
  "Initializing node registration...",
  `Resolving endpoint: ${nodeName}...`,
  "Generating cryptographic keys...",
  "Detecting node location...",
  "Probing address reachability...",
  "Packaging agent binary...",
];

export default function PreparingStep({ nodeName }) {
  const [progress, setProgress] = useState(0);
  const log = useMemo(() => buildLog(nodeName), [nodeName]);

  // Asymptotic approach to CEILING. Each tick closes a fraction of the remaining
  // gap, so it decelerates and never lands — the visual equivalent of "still
  // working, duration unknown".
  useEffect(() => {
    const id = setInterval(
      () => setProgress((p) => p + (CEILING - p) * EASE),
      TICK_MS,
    );
    return () => clearInterval(id);
  }, []);

  // Reveal lines as the bar advances. They describe what the endpoint is actually
  // doing, in the order it does it.
  const shown = log.slice(0, Math.min(log.length, Math.floor(progress / (CEILING / log.length)) + 1));
  const pct = Math.floor(progress);

  return (
    <div className={styles.step}>
      <div className={styles.stepHead}>
        <h2 className={styles.title} id="add-node-title">Registering Node</h2>
        <p className={styles.subtitle}>Creating {nodeName} — please wait.</p>
      </div>

      <div className={styles.console} role="log" aria-live="polite">
        {shown.map((line) => (
          <div key={line} className={styles.consoleLine}>
            <span className={styles.consoleCaret}>›</span>
            {line}
          </div>
        ))}
        <div className={styles.consoleLine}>
          <span className={styles.consoleCaret}>›</span>
          <span className={styles.cursor} />
        </div>
      </div>

      <div className={styles.progress}>
        <div className={styles.progressHead}>
          <span className={styles.progressLabel}>Progress</span>
          <span className={styles.progressPct}>{String(pct).padStart(2, "0")}%</span>
        </div>
        {/* No aria-valuenow: this is indeterminate, and publishing a number a
            screen reader would read as real progress would be a lie. */}
        <div className={styles.progressTrack} role="progressbar" aria-busy="true">
          <span className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className={styles.working}>
        <span className={styles.spinner}><SpinnerIcon /></span>
        Communicating with control plane...
      </div>
    </div>
  );
}
